import OnPremPortOpeningObject from './OnPremPortOpeningObject';
import WorkflowInstance from '../OnPremWorkflowInstance';
import User from '../User';
import { WorkflowState } from '../enums/WorkflowState';
import { authenticatedUser } from '../../authentication/authModule';
import { UserRole } from '../enums/UserRole';
import { PortOpeningRequestsDirection } from '../enums/POR/PortOpeningRequestsDirection';
import CartaId from './CartaId';
import { OnPremPortOpeningRequestsTypes } from '../enums/POR/OnPremPortOpeningRequestsTypes';
import CartaIdComponent from '../viewModels/CartaId/CartaIdComponent';

export default class OnPremPORequestObject {
	id!: number;

	type!: OnPremPortOpeningRequestsTypes;

	trackerID!: number;

	firewall!: string; //Firewall

	firewallEnvironment!: string;

	siteCoordination!: boolean;

	cartaId!: CartaId[];

	appEndorser!: CartaIdComponent;

	approverUser!: User;

	creationDate!: Date;

	justification!: string;

	onPremObjects!: OnPremPortOpeningObject[];

	onPremWorkflowInstance!: WorkflowInstance;

	direction!: PortOpeningRequestsDirection;

	projectAccountUser!: User;

	projectId!: string;

	technicalCoordinator!: User;

	changeTicketId?: string;

	approvingUser!: User;

	approvingEmail!: string;

	ownerDelegateUser!: string;

	constructor(obj: any) {
		Object.assign(this, obj);
		this.onPremWorkflowInstance = new WorkflowInstance(obj.onPremWorkflowInstance);
		this.type = new OnPremPortOpeningRequestsTypes().fromName(obj.type?.name);
		this.direction = new PortOpeningRequestsDirection().fromName(obj.direction?.name);
		this.approvingUser = obj.approverUser
		if (this.type === OnPremPortOpeningRequestsTypes.InternetDMZ) {
			const customFields = new CartaIdComponent(obj.appEndorser).fromUser(obj.appEndorser);
			this.appEndorser = new CartaIdComponent({
				...obj.appEndorser,
				customFields: customFields,
			});
		}
	}

	private currentState = () => this.onPremWorkflowInstance?.currentWorkflowInstanceState?.onPremWorkflowState?.value;

	public isInState = (...states: WorkflowState[]) => states.map((state) => state.value).includes(this.currentState());

	isEditable = () =>
		authenticatedUser.userId === this.onPremWorkflowInstance.createdBy.id &&
		this.isInState(WorkflowState.Draft, WorkflowState.Review);

	updatedAt = (): Date => new Date(this.onPremWorkflowInstance.currentWorkflowInstanceState.performedOnDate);

	canBeCancelled = () =>
		this.isInState(
			WorkflowState.Draft,
			WorkflowState.Review,
			WorkflowState.PendingForPolicyTeamApproval,
			WorkflowState.PendingSolutionArchitectureApproval,
			WorkflowState.PendingForCyberApproval,
			WorkflowState.PendingOTCRMApproval,
			WorkflowState.PendingConnOwnerApproval,
			WorkflowState.PendingAppEndorserApproval,
			WorkflowState.PendingProxyArchitectApproval,
			WorkflowState.PendingForDOAGApproval
		);

	canHaveAppOwnerApproval = () =>
		this.isInState(WorkflowState.PendingAppEndorserApproval) &&
		authenticatedUser.userEmail === this.appEndorser?.customFields.contact_email;

	canHaveDOAGApproval = () =>
		this.isInState(WorkflowState.PendingForDOAGApproval) && authenticatedUser.userId === this.approverUser?.id;

	canHaveReviewerApproval = () =>
		this.isInState(WorkflowState.PendingForPolicyTeamApproval) &&
		authenticatedUser.isInRole(UserRole.PORToOnPremTechReviewer);

	canHaveConnOwnerApproval = () =>
		this.isInState(WorkflowState.PendingConnOwnerApproval) && authenticatedUser.userId === this.approverUser?.id;

	canHaveImplementerApproval = () =>
		this.isInState(WorkflowState.AwaitingImplementation) &&
		authenticatedUser.isInRole(UserRole.PORToOnPremTechImplementer);

	canHaveProxyApproval = () =>
		this.isInState(WorkflowState.AwaitingImplementation) &&
		authenticatedUser.isInRole(UserRole.POROnPremIBossTechnicalImplementer);

	canHaveOTCRMApproval = () =>
		this.isInState(WorkflowState.PendingOTCRMApproval) && authenticatedUser.isInRole(UserRole.PORToOnPremOTCRM);

	canHaveCyberSecApproval = () =>
		this.isInState(WorkflowState.PendingForCyberApproval) &&
		authenticatedUser.isInRole(UserRole.PORToOnPremCyberReviewer);

	canHaveSolutionArchitectApproval = () =>
		this.isInState(WorkflowState.PendingSolutionArchitectureApproval) &&
		authenticatedUser.isInRole(UserRole.POROnPremSolArqReviewer);

	canHaveProxyArchApproval = () =>
		this.isInState(WorkflowState.PendingProxyArchitectApproval) &&
		authenticatedUser.isInRole(UserRole.POROnPremProxyArchitectReviewer);

	canEscalateApproval = () =>
		this.isInState(WorkflowState.PendingForPolicyTeamApproval) &&
		authenticatedUser.isInRole(UserRole.PORToOnPremTechReviewer);

	canEscalateToCyberSec = () =>
		this.isInState(WorkflowState.PendingSolutionArchitectureApproval) &&
		authenticatedUser.isInRole(UserRole.POROnPremSolArqReviewer);

	canContinueRequest = () => this.isInState(WorkflowState.Completed, WorkflowState.ImplementationFailed);

	canSendToReview = () =>
		(this.isInState(WorkflowState.PendingForPolicyTeamApproval) &&
			authenticatedUser.isInRole(UserRole.PORToOnPremTechReviewer)) ||
		(this.isInState(WorkflowState.PendingSolutionArchitectureApproval) &&
			authenticatedUser.isInRole(UserRole.POROnPremSolArqReviewer)) ||
		(this.isInState(WorkflowState.PendingForCyberApproval) &&
			authenticatedUser.isInRole(UserRole.PORToOnPremCyberReviewer)) ||
		(this.isInState(WorkflowState.PendingProxyArchitectApproval) &&
			authenticatedUser.isInRole(UserRole.POROnPremProxyArchitectReviewer)) ||
		(this.isInState(WorkflowState.PendingOTCRMApproval) &&
			authenticatedUser.isInRole(UserRole.PORToOnPremOTCRM) &&
			this.type === OnPremPortOpeningRequestsTypes.InternalCertConnection) ||
		(this.isInState(WorkflowState.PendingAppEndorserApproval) &&
			authenticatedUser.userId === this.appEndorser?.id &&
			this.type === OnPremPortOpeningRequestsTypes.InternetDMZ) ||
		(this.isInState(WorkflowState.PendingConnOwnerApproval) &&
			authenticatedUser.userId === this.approverUser?.id &&
			this.type === OnPremPortOpeningRequestsTypes.InternalCertConnection);
}
